const DEFAULT_COLOR = '#ffd54f'
const DEFAULT_OPACITY = 0.28
const ASPECT_MATCH_TOLERANCE = 0.002

const ORIENTATION_TO_PROFILE = {
  p: 'portrait',
  portrait: 'portrait',
  l: 'landscape',
  landscape: 'landscape'
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max)
}

function toNumber(value) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function normalizeDimensionsForOrientation(profileKey, width, height) {
  if (profileKey === 'landscape' && height > width) {
    return {
      width: height,
      height: width
    }
  }

  // Portrait note: we intentionally do NOT swap width/height here.
  // Portrait screens (e.g. Orange 840x1080) fit content by height and show a
  // centered width-slice, exactly like landscape. Uploads come in two shapes:
  //   - native 840x1080 (width < height)
  //   - 16:9 1920x1080 where the real content sits centered in the 840x1080 region
  // Both must map to the same centered 722x1080 safe area. Swapping a genuine
  // 1920x1080 upload to 1080x1920 produced a tall, narrow pillarbox frame
  // (awkward, didn't match playback), so we keep the dimensions as-is and let
  // the standard fit-by-height crop logic centre the safe area.

  return {
    width,
    height
  }
}

function toPercent(ratio) {
  return `${(ratio * 100).toFixed(3).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1')}%`
}

function normalizeProfile(profileKey, profile) {
  if (!profile || !profile.enabled) {
    return null
  }

  const width = toNumber(profile.width)
  const height = toNumber(profile.height)

  if (!width || !height || width <= 0 || height <= 0) {
    return null
  }

  const opacity = toNumber(profile.opacity)

  return {
    key: profileKey,
    width,
    height,
    aspectRatio: width / height,
    label: profile.label || '',
    color: profile.color || DEFAULT_COLOR,
    opacity: clamp(opacity === null ? DEFAULT_OPACITY : opacity, 0, 1)
  }
}

function convertHexColor(color, opacity) {
  const sanitized = color.replace('#', '')
  const normalized = sanitized.length === 3
    ? sanitized.split('').map(part => part + part).join('')
    : sanitized

  if (normalized.length !== 6) {
    return null
  }

  const red = parseInt(normalized.slice(0, 2), 16)
  const green = parseInt(normalized.slice(2, 4), 16)
  const blue = parseInt(normalized.slice(4, 6), 16)

  if ([red, green, blue].some(Number.isNaN)) {
    return null
  }

  return `rgba(${red}, ${green}, ${blue}, ${opacity})`
}

export function applyOpacity(color, opacity) {
  if (!color) {
    return color
  }

  if (color.startsWith('#')) {
    return convertHexColor(color, opacity) || color
  }

  if (color.startsWith('rgba(')) {
    const channels = color.slice(5, -1).split(',').map(part => part.trim())
    if (channels.length === 4) {
      return `rgba(${channels[0]}, ${channels[1]}, ${channels[2]}, ${opacity})`
    }
  }

  if (color.startsWith('rgb(')) {
    const channels = color.slice(4, -1).split(',').map(part => part.trim())
    if (channels.length === 3) {
      return `rgba(${channels[0]}, ${channels[1]}, ${channels[2]}, ${opacity})`
    }
  }

  return color
}

export function getPreviewSafeAreaProfile(previewSafeAreaConfig, orientation) {
  const profileKey = ORIENTATION_TO_PROFILE[orientation]
  if (!profileKey || !previewSafeAreaConfig) {
    return null
  }

  return normalizeProfile(profileKey, previewSafeAreaConfig[profileKey])
}

export function buildPreviewSafeAreaOverlay(previewSafeAreaConfig, orientation, containerWidth, containerHeight) {
  const profile = getPreviewSafeAreaProfile(previewSafeAreaConfig, orientation)
  const rawWidth = toNumber(containerWidth)
  const rawHeight = toNumber(containerHeight)

  if (!profile || !rawWidth || !rawHeight || rawWidth <= 0 || rawHeight <= 0) {
    return null
  }

  const normalizedDimensions = normalizeDimensionsForOrientation(profile.key, rawWidth, rawHeight)
  const width = normalizedDimensions.width
  const height = normalizedDimensions.height


  const containerAspect = width / height
  const aspectDelta = Math.abs((containerAspect / profile.aspectRatio) - 1)

  // When the preview already matches the configured safe-area aspect, the box
  // would cover the full frame and add no information, so hide it entirely.
  if (aspectDelta <= ASPECT_MATCH_TOLERANCE) {
    return null
  }

  const maskColor = applyOpacity(profile.color, profile.opacity)
  const fillColor = applyOpacity(profile.color, Math.min(profile.opacity, 0.12))
  const outlineColor = applyOpacity(profile.color, 0.95)

  const baseOverlay = {
    ...profile,
    rawContainerWidth: rawWidth,
    rawContainerHeight: rawHeight,
    containerWidth: width,
    containerHeight: height
  }

  if (containerAspect > profile.aspectRatio) {
    // CROP: the content is wider than the screen, so the physical screen only
    // shows a centered, full-height slice and crops the sides. The safe-area box
    // marks the visible slice; the darkened sides are what gets cut off.
    const widthRatio = clamp(profile.aspectRatio / containerAspect, 0, 1)
    const heightRatio = 1
    const leftRatio = (1 - widthRatio) / 2
    const topRatio = 0

    return {
      ...baseOverlay,
      mode: 'crop',
      widthRatio,
      heightRatio,
      leftRatio,
      rightRatio: leftRatio,
      topRatio,
      bottomRatio: topRatio,
      videoWidthRatio: 1,
      barRatio: 0,
      frameStyle: null,
      boxStyle: {
        left: toPercent(leftRatio),
        top: toPercent(topRatio),
        width: toPercent(widthRatio),
        height: toPercent(heightRatio),
        borderColor: outlineColor,
        backgroundColor: fillColor,
        boxShadow: `0 0 0 9999px ${maskColor}`
      }
    }
  }

  // PILLARBOX: the content is narrower than the screen, so on the real screen it
  // is centered with black bars on the sides. We letterbox the preview to match:
  // the frame represents the full screen and the safe-area box outlines it, with
  // the (narrower) video centered inside and black bars exposed on each side.
  const videoWidthRatio = clamp(containerAspect / profile.aspectRatio, 0, 1)
  const barRatio = (1 - videoWidthRatio) / 2
  // The exposed side bars sit on the screen's black area, so a low-opacity tint
  // would read as near-black. Use a strong, clearly yellow fill so the "video not
  // wide enough" bars are obvious.
  const barColor = applyOpacity(profile.color, Math.max(profile.opacity, 0.85))

  return {
    ...baseOverlay,
    mode: 'pillarbox',
    widthRatio: 1,
    heightRatio: 1,
    leftRatio: 0,
    rightRatio: 0,
    topRatio: 0,
    bottomRatio: 0,
    videoWidthRatio,
    barRatio,
    frameStyle: {
      aspectRatio: profile.aspectRatio
    },
    leftBarStyle: {
      left: '0%',
      top: '0%',
      width: toPercent(barRatio),
      height: '100%',
      backgroundColor: barColor
    },
    rightBarStyle: {
      left: toPercent(1 - barRatio),
      top: '0%',
      width: toPercent(barRatio),
      height: '100%',
      backgroundColor: barColor
    },
    boxStyle: {
      left: '0%',
      top: '0%',
      width: '100%',
      height: '100%',
      borderColor: outlineColor,
      backgroundColor: 'transparent',
      boxShadow: 'none'
    }
  }
}

export function formatPreviewSafeAreaLabel(overlay) {
  if (!overlay) {
    return ''
  }

  const fallbackLabel = overlay.key === 'portrait' ? 'Portrait safe area' : 'Landscape safe area'
  return overlay.label || fallbackLabel
}

export function formatPreviewSafeAreaTooltip(overlay) {
  if (!overlay) {
    return ''
  }

  const aspect = `${(overlay.width / overlay.height).toFixed(2)}:1`
  return `${overlay.width}x${overlay.height} (${aspect})`
}
